<template>
  <div class="flex flex-col lg:flex-row w-full relative">
    <!-- Top panel -->
    <div
      class="flex lg:hidden flex-col w-full min-w-0 overflow-x-hidden space-y-4 bg-white shadow-2xl rounded-3xl items-center"
    >
      <div
        @click="showImageUpdate = true"
        class="w-24 h-24 rounded-full overflow-hidden cursor-pointer relative group"
      >
        <img
          :src="userImage ? userImage : profileImage"
          alt="Image"
          class="w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 bg-slate-300 bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center w-full h-full"
        >
          <i
            class="pi pi-camera text-white text-2xl opacity-0 group-hover:opacity-100 transition-all duration-300"
          ></i>
        </div>
      </div>
      <div
        :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        class="flex flex-row items-center justify-center gap-x-1 w-full max-w-full min-w-0 px-2"
      >
        <p
          class="text-base text-slate-800 font-bold truncate min-w-0 flex-1"
          :class="locale === 'ar' ? 'text-start' : 'text-center'"
          :title="fullName"
        >
          {{ fullName }}
        </p>
        <img
          v-if="isVerified"
          src="../assets/images/settings/verified.svg"
          alt=""
          class="w-5 shrink-0 text-white fill-white"
        />
      </div>
      <div class="container flex flex-row pb-6">
        <button
          @click="toggleTab(0)"
          class="container flex flex-col space-x-2 items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            :class="{
              'fill-[#4867C8]': tabIndex == 0,
              'fill-slate-800': tabIndex != 0,
            }"
            class="h-[22px] w-[22px] md:h-[24px] md:w-[24px]"
          >
            <path
              d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm0 22c-3.123 0-5.914-1.441-7.749-3.69.259-.588.783-.995 1.867-1.246 2.244-.518 4.459-.981 3.393-2.945-3.155-5.82-.899-9.119 2.489-9.119 3.322 0 5.634 3.177 2.489 9.119-1.035 1.952 1.1 2.416 3.393 2.945 1.082.25 1.61.655 1.871 1.241-1.836 2.253-4.628 3.695-7.753 3.695z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 0,
              'text-slate-800': tabIndex != 0,
            }"
            class="font-semibold text-xs lg:text-xl"
          >
            {{ $t("Account_Settings") }}
          </p>
          <div
            :class="{ 'opacity-0': tabIndex != 0 }"
            class="bg-[#4867C8] h-[3px] w-[60%] hidden md:block"
          ></div>
        </button>
        <button
          @click="toggleTab(5)"
          class="container flex flex-col space-x-2 items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            :class="{
              'fill-[#4867C8]': tabIndex == 5,
              'fill-slate-800': tabIndex != 5,
            }"
            class="h-[22px] w-[22px] md:h-[24px] md:w-[24px]"
          >
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></g>
            <path
              d="M8.625 0c.61 7.189-5.625 9.664-5.625 15.996 0 4.301 3.069 7.972 9 8.004 5.931.032 9-4.414 9-8.956 0-4.141-2.062-8.046-5.952-10.474.924 2.607-.306 4.988-1.501 5.808.07-3.337-1.125-8.289-4.922-10.378zm4.711 13c3.755 3.989 1.449 9-1.567 9-1.835 0-2.779-1.265-2.769-2.577.019-2.433 2.737-2.435 4.336-6.423z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 5,
              'text-slate-800': tabIndex != 5,
            }"
            class="font-semibold text-xs lg:text-xl"
          >
            {{ $t("subscription") }}
          </p>
          <div
            :class="{ 'opacity-0': tabIndex != 5 }"
            class="bg-[#4867C8] h-[3px] w-[60%] hidden md:block"
          ></div>
        </button>
        <button
          v-if="!authStore.authUsingGoogle && !(isVerified && isStudent)"
          @click="toggleTab(4)"
          class="container flex flex-col space-x-2 items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            :class="{
              'fill-[#4867C8]': tabIndex == 4,
              'fill-slate-800': tabIndex != 4,
            }"
            class="h-[22px] w-[22px] md:h-[24px] md:w-[24px]"
          >
            <path
              d="M13.718 10.528c0 .792-.268 1.829-.684 2.642-1.009 1.98-3.063 1.967-3.063-.14 0-.786.27-1.799.687-2.58 1.021-1.925 3.06-1.624 3.06.078zm10.282 1.472c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-5-1.194c0-3.246-2.631-5.601-6.256-5.601-4.967 0-7.744 3.149-7.744 7.073 0 3.672 2.467 6.517 7.024 6.517 2.52 0 4.124-.726 5.122-1.288l-.687-.991c-1.022.593-2.251 1.136-4.256 1.136-3.429 0-5.733-2.199-5.733-5.473 0-5.714 6.401-6.758 9.214-5.071 2.624 1.642 2.524 5.578.582 7.083-1.034.826-2.199.799-1.821-.756 0 0 1.212-4.489 1.354-4.975h-1.364l-.271.952c-.278-.785-.943-1.295-1.911-1.295-2.018 0-3.722 2.19-3.722 4.783 0 1.73.913 2.804 2.38 2.804 1.283 0 1.95-.726 2.364-1.373-.3 2.898 5.725 1.557 5.725-3.525z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 4,
              'text-slate-800': tabIndex != 4,
            }"
            class="font-semibold text-xs lg:text-xl"
          >
            {{ $t("Contact") }}
          </p>
          <div
            :class="{ 'opacity-0': tabIndex != 4 }"
            class="bg-[#4867C8] h-[3px] w-[60%] hidden md:block"
          ></div>
        </button>
        <button
          v-if="!authStore.authUsingGoogle"
          @click="toggleTab(1)"
          class="container flex flex-col space-x-2 items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            :class="{
              'fill-[#4867C8]': tabIndex == 1,
              'fill-slate-800': tabIndex != 1,
            }"
            class="h-[22px] w-[22px] md:h-[24px] md:w-[24px]"
          >
            <path
              d="M12 8c-1.062 0-2.073.211-3 .587v-3.587c0-1.654 1.346-3 3-3s3 1.346 3 3v1h2v-1c0-2.761-2.238-5-5-5-2.763 0-5 2.239-5 5v4.761c-1.827 1.466-3 3.714-3 6.239 0 4.418 3.582 8 8 8s8-3.582 8-8-3.582-8-8-8zm0 10c-1.104 0-2-.896-2-2s.896-2 2-2 2 .896 2 2-.896 2-2 2z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 1,
              'text-slate-800': tabIndex != 1,
            }"
            class="font-semibold text-xs lg:text-xl"
          >
            {{ $t("security-settings") }}
          </p>
          <div
            :class="{ 'opacity-0': tabIndex != 1 }"
            class="bg-[#4867C8] h-[3px] w-[60%] hidden md:block"
          ></div>
        </button>
        <button
          @click="toggleTab(3)"
          class="container flex flex-col space-x-2 items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            :class="{
              'fill-[#4867C8]': tabIndex == 3,
              'fill-slate-800': tabIndex != 3,
            }"
            class="h-[22px] w-[22px] md:h-[24px] md:w-[24px]"
          >
            <path
              d="M22 4h-20c-1.104 0-2 .896-2 2v12c0 1.104.896 2 2 2h20c1.104 0 2-.896 2-2v-12c0-1.104-.896-2-2-2zm-16 13h-3v-1h3v1zm5 0h-3v-1h3v1zm.32-4.377c-.383.239-.836.377-1.32.377-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5c.484 0 .937.138 1.32.377-.531.552-.857 1.3-.857 2.123 0 .824.326 1.571.857 2.123zm4.68 4.377h-3v-1h3v1zm-2-4c-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5 2.5 1.119 2.5 2.5-1.119 2.5-2.5 2.5zm7 4h-3v-1h3v1z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 3,
              'text-slate-800': tabIndex != 3,
            }"
            class="font-semibold text-xs lg:text-xl"
          >
            {{ $t("payments") }}
          </p>
          <div
            :class="{ 'opacity-0': tabIndex != 3 }"
            class="bg-[#4867C8] h-[3px] w-[60%] hidden md:block"
          ></div>
        </button>
      </div>
    </div>
    <!-- Sidebar (order in row follows page direction; menu uses same DOM order + dir for RTL) -->
    <div
      :dir="locale === 'ar' ? 'rtl' : 'ltr'"
      class="hidden lg:flex flex-col w-[20%] min-w-0 overflow-x-hidden overflow-y-visible space-y-4 bg-white shadow-lg items-center h-auto px-2 lg:px-3"
    >
      <div
        @click="showImageUpdate = true"
        class="w-24 h-24 rounded-full overflow-hidden cursor-pointer relative group"
      >
        <img
          :src="userImage ? userImage : profileImage"
          alt="Image"
          class="w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 bg-slate-300 bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center w-full h-full"
        >
          <i
            class="pi pi-camera text-white text-2xl opacity-0 group-hover:opacity-100 transition-all duration-300"
          ></i>
        </div>
      </div>
      <div
        :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        class="flex flex-row items-center justify-center gap-x-1 w-full max-w-full min-w-0 px-1"
      >
        <p
          class="text-base text-slate-800 font-bold truncate min-w-0 flex-1"
          :class="locale === 'ar' ? 'text-start' : 'text-center'"
          :title="fullName"
        >
          {{ fullName }}
        </p>
        <img
          v-if="isVerified"
          src="../assets/images/settings/verified.svg"
          alt=""
          class="w-5 shrink-0 text-white fill-white"
        />
      </div>
      <div class="flex w-full min-w-0 flex-col gap-1">
        <button
          @click="toggleTab(0)"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            :class="{ 'opacity-0': tabIndex != 0 }"
            class="shrink-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            width="24"
            height="24"
            class="shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            :class="{
              'fill-[#4867C8]': tabIndex == 0,
              'fill-slate-800': tabIndex != 0,
            }"
          >
            <path
              d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm0 22c-3.123 0-5.914-1.441-7.749-3.69.259-.588.783-.995 1.867-1.246 2.244-.518 4.459-.981 3.393-2.945-3.155-5.82-.899-9.119 2.489-9.119 3.322 0 5.634 3.177 2.489 9.119-1.035 1.952 1.1 2.416 3.393 2.945 1.082.25 1.61.655 1.871 1.241-1.836 2.253-4.628 3.695-7.753 3.695z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 0,
              'text-slate-800': tabIndex != 0,
            }"
            class="font-semibold text-xl min-w-0 leading-snug text-start"
          >
            {{ $t("profile") }}
          </p>
        </button>
        <button
          @click="toggleTab(5)"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            :class="{ 'opacity-0': tabIndex != 5 }"
            class="shrink-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            width="24"
            height="24"
            class="shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            :class="{
              'fill-[#4867C8]': tabIndex == 5,
              'fill-slate-800': tabIndex != 5,
            }"
          >
            <path
              d="M8.625 0c.61 7.189-5.625 9.664-5.625 15.996 0 4.301 3.069 7.972 9 8.004 5.931.032 9-4.414 9-8.956 0-4.141-2.062-8.046-5.952-10.474.924 2.607-.306 4.988-1.501 5.808.07-3.337-1.125-8.289-4.922-10.378zm4.711 13c3.755 3.989 1.449 9-1.567 9-1.835 0-2.779-1.265-2.769-2.577.019-2.433 2.737-2.435 4.336-6.423z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 5,
              'text-slate-800': tabIndex != 5,
            }"
            class="font-semibold text-xl min-w-0 leading-snug text-start"
          >
            {{ $t("subscription") }}
          </p>
        </button>
        <button
          v-if="!authStore.authUsingGoogle && !(isVerified && isStudent)"
          @click="toggleTab(4)"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            :class="{ 'opacity-0': tabIndex != 4 }"
            class="shrink-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            width="24"
            height="24"
            class="shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            :class="{
              'fill-[#4867C8]': tabIndex == 4,
              'fill-slate-800': tabIndex != 4,
            }"
          >
            <path
              d="M13.718 10.528c0 .792-.268 1.829-.684 2.642-1.009 1.98-3.063 1.967-3.063-.14 0-.786.27-1.799.687-2.58 1.021-1.925 3.06-1.624 3.06.078zm10.282 1.472c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-5-1.194c0-3.246-2.631-5.601-6.256-5.601-4.967 0-7.744 3.149-7.744 7.073 0 3.672 2.467 6.517 7.024 6.517 2.52 0 4.124-.726 5.122-1.288l-.687-.991c-1.022.593-2.251 1.136-4.256 1.136-3.429 0-5.733-2.199-5.733-5.473 0-5.714 6.401-6.758 9.214-5.071 2.624 1.642 2.524 5.578.582 7.083-1.034.826-2.199.799-1.821-.756 0 0 1.212-4.489 1.354-4.975h-1.364l-.271.952c-.278-.785-.943-1.295-1.911-1.295-2.018 0-3.722 2.19-3.722 4.783 0 1.73.913 2.804 2.38 2.804 1.283 0 1.95-.726 2.364-1.373-.3 2.898 5.725 1.557 5.725-3.525z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 4,
              'text-slate-800': tabIndex != 4,
            }"
            class="font-semibold text-xl min-w-0 leading-snug text-start"
          >
            {{ $t("Contact") }}
          </p>
        </button>
        <button
          v-if="!authStore.authUsingGoogle"
          @click="toggleTab(1)"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            :class="{ 'opacity-0': tabIndex != 1 }"
            class="shrink-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            width="24"
            height="24"
            class="shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            :class="{
              'fill-[#4867C8]': tabIndex == 1,
              'fill-slate-800': tabIndex != 1,
            }"
          >
            <path
              d="M12 8c-1.062 0-2.073.211-3 .587v-3.587c0-1.654 1.346-3 3-3s3 1.346 3 3v1h2v-1c0-2.761-2.238-5-5-5-2.763 0-5 2.239-5 5v4.761c-1.827 1.466-3 3.714-3 6.239 0 4.418 3.582 8 8 8s8-3.582 8-8-3.582-8-8-8zm0 10c-1.104 0-2-.896-2-2s.896-2 2-2 2 .896 2 2-.896 2-2 2z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 1,
              'text-slate-800': tabIndex != 1,
            }"
            class="font-semibold text-xl min-w-0 leading-snug text-start"
          >
            {{ $t("security") }}
          </p>
        </button>
        <button
          @click="toggleTab(3)"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            :class="{ 'opacity-0': tabIndex != 3 }"
            class="shrink-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            width="24"
            height="24"
            class="shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            :class="{
              'fill-[#4867C8]': tabIndex == 3,
              'fill-slate-800': tabIndex != 3,
            }"
          >
            <path
              d="M22 4h-20c-1.104 0-2 .896-2 2v12c0 1.104.896 2 2 2h20c1.104 0 2-.896 2-2v-12c0-1.104-.896-2-2-2zm-16 13h-3v-1h3v1zm5 0h-3v-1h3v1zm.32-4.377c-.383.239-.836.377-1.32.377-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5c.484 0 .937.138 1.32.377-.531.552-.857 1.3-.857 2.123 0 .824.326 1.571.857 2.123zm4.68 4.377h-3v-1h3v1zm-2-4c-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5 2.5 1.119 2.5 2.5-1.119 2.5-2.5 2.5zm7 4h-3v-1h3v1z"
            />
          </svg>
          <p
            :class="{
              'text-[#4867C8]': tabIndex == 3,
              'text-slate-800': tabIndex != 3,
            }"
            class="font-semibold text-xl min-w-0 leading-snug text-start"
          >
            {{ $t("payments") }}
          </p>
        </button>
        <button
          @click="logout"
          type="button"
          class="flex w-full min-w-0 flex-row items-center gap-2 pt-4"
        >
          <div
            class="shrink-0 opacity-0 bg-[#4867C8] h-8 w-1 self-center rounded-full"
          ></div>
          <svg
            class="fill-red-500 shrink-0"
            width="24"
            height="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 9v-4l8 7-8 7v-4h-8v-6h8zm-2 10v-.083c-1.178.685-2.542 1.083-4 1.083-4.411 0-8-3.589-8-8s3.589-8 8-8c1.458 0 2.822.398 4 1.083v-2.245c-1.226-.536-2.577-.838-4-.838-5.522 0-10 4.477-10 10s4.478 10 10 10c1.423 0 2.774-.302 4-.838v-2.162z"
            />
          </svg>
          <p
            class="font-semibold text-xl text-red-500 min-w-0 leading-snug text-start"
          >
            {{ $t("logout") }}
          </p>
        </button>
      </div>
    </div>
    <!-- right panel -->
    <div
      v-if="!isLoading"
      class="relative flex flex-col items-center w-full lg:w-[80%] ps-0 lg:ps-20 pt-1"
    >
      <!-- profile tab -->
      <div
        v-if="tabIndex == 0"
        :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        class="w-[90%] space-y-4 pt-10 lg:pt-0"
      >
        <p class="text-[#061B44] font-bold text-base md:text-2xl text-start">
          {{ $t("Account_Settings") }}
        </p>
        <p class="text-sm text-slate-500 text-start mt-1 max-w-xl">
          {{ $t("profile-photo-change-hint") }}
        </p>
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 mt-6"
        >
          <div class="flex flex-col container w-full">
            <p class="text-base text-slate-500 font-medium pb-4 text-start">
              {{ $t("First_name") }}
            </p>
            <input
              v-model="fname"
              type="text"
              :maxlength="PROFILE_NAME_MAX_LENGTH"
              class="bg-gray-50 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-[95%] lg:w-full p-4 h-10 text-start"
              :placeholder="$t('first-name')"
              required="true"
            />
          </div>
          <div class="flex flex-col container w-full pt-4">
            <p class="text-base text-slate-500 font-medium pb-4 text-start">
              {{ $t("Last_name") }}
            </p>
            <input
              v-model="lname"
              type="text"
              :maxlength="PROFILE_NAME_MAX_LENGTH"
              class="bg-gray-50 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-[95%] lg:w-full p-4 h-10 text-start"
              :placeholder="$t('last-name')"
              required="true"
            />
          </div>
          <p class="text-xs text-slate-500 pt-1 text-start">
            {{
              $t("profile-name-max-hint", {
                max: PROFILE_NAME_MAX_LENGTH,
              })
            }}
          </p>

          <!-- <p v-if="isProfileError" class="font-semibold text-red-500 text-lg">
            {{ errorProfileMessage }}
          </p> -->
          <div
            class="container flex flex-row flex-wrap justify-between gap-4 pt-6"
          >
            <button
              @click="updateProfile"
              type="button"
              class="w-[45%] lg:w-[35%] text-white flex justify-center items-center space-x-1 lg:space-x-4 bg-[#6193F6] h-10 px-2 py-1 lg:px-3 rounded-lg"
            >
              <div class="flex flex-row items-center justify-center">
                <p
                  :class="{ 'px-2': locale === 'ar' }"
                  class="font-semibold text-base md:text-lg"
                >
                  {{ $t("save") }}
                </p>
              </div>
            </button>
            <div v-if="locale === 'ar'" class="w-2"></div>
            <button
              @click="fetchProfile"
              type="button"
              :disabled="isLoading"
              class="w-[45%] lg:w-[35%] flex justify-center items-center space-x-1 lg:space-x-4 bg-white text-slate-900 border-slate-400 border-2 h-10 px-2 py-1 lg:px-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div class="flex flex-row items-center justify-center">
                <p
                  :class="{ 'px-2': locale === 'ar' }"
                  class="font-semibold text-base md:text-lg"
                >
                  {{ $t("cancel") }}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <!-- susbcription tab -->
      <div v-if="tabIndex == 5" class="w-[90%] pt-10 lg:pt-0">
        <p class="text-[#061B44] font-bold text-base md:text-2xl">
          {{ $t("subscription-management") }}
        </p>
        <!-- <p class="text-[#061B44] font-semibold text-base">
          {{ $t("view-and-manage-your-subscription-details") }}
        </p> -->
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 mt-4"
        >
          <div class="flex flex-row space-x-2 items-center">
            <p class="text-[#061B44] font-semibold text-base md:text-2xl">
              {{ $t("Current_Plan_Information") }}
            </p>
            <!-- "Main source" badge hidden (deposit / main-source UX removed)
            <div
              v-if="userStore.subscriptionType !== 'CONSUMEASYOUPAY'"
              class="text-blue-700 flex flex-row font-medium items-center space-x-1 bg-blue-100 py-1 px-4 w-min text-nowrap text-xs h-min rounded-full"
            >
              <i
                class="pi pi-star-fill text-blue-500 text-xs cursor-pointer hover:text-slate-800"
              ></i>
              <p>{{ $t("main-source") }}</p>
            </div>
            -->
          </div>
          <div class="flex flex-row mt-4">
            <div class="flex flex-col space-y-1 w-[50%]">
              <p class="text-sm md:text-base text-slate-500 font-medium">
                {{ $t("plan") }}
              </p>
              <p class="text-base md:text-lg text-green-500 font-semibold">
                {{
                  locale === "ar"
                    ? $t("package") +
                      " " +
                      capitalizeFirstLetter(
                        $t(getLastPackage(userStore.subscriptionType.toLowerCase()))
                      )
                    : capitalizeFirstLetter(
                        $t(getLastPackage(userStore.subscriptionType.toLowerCase()))
                      ) +
                      " " +
                      $t("package")
                }}
              </p>
            </div>
            <div
              v-if="
                userStore.subscriptionType != 'FREE' &&
                userStore.subscriptionStartDate
              "
              class="flex flex-col space-y-1 w-[50%]"
            >
              <p class="text-sm md:text-base text-slate-500 font-medium">
                {{ $t("status") }}
              </p>
              <p
                :class="{
                  'text-green-500': !isExpired(),
                  'text-red-500': isExpired(),
                }"
                class="text-base md:text-lg font-semibold"
              >
                {{ isExpired() ? $t("expired") : $t("active") }}
              </p>
            </div>
            <button
              v-else
              @click="navigate('pricing')"
              class="bg-[#6193F6] text-white font-semibold py-1 px-1 md:px-6 rounded-lg w-[80%] lg:w-[45%] h-10 mt-4"
            >
              {{ $t("upgrade-subscription") }}
            </button>
          </div>
          <div
            v-if="
              userStore.subscriptionType != 'FREE' &&
              userStore.subscriptionStartDate
            "
            class="flex flex-row mt-4"
          >
            <div class="flex flex-col space-y-1 w-[50%]">
              <p class="text-sm md:text-base text-slate-500 font-medium">
                {{ $t("started-on") }}
              </p>
              <p class="text-base md:text-lg text-slate-700 font-semibold">
                {{ formatDate(userStore.subscriptionStartDate) }}
              </p>
            </div>
            <div
              v-if="userStore.subscriptionType != 'FREE'"
              class="flex flex-col space-y-1 w-[50%]"
            >
              <p class="text-sm md:text-base text-slate-500 font-medium">
                {{ $t("expires-on") }}
              </p>
              <p class="text-base md:text-lg text-slate-700 font-semibold">
                {{ formatDate(userStore.subscriptionEndDate) }}
              </p>
            </div>
          </div>
          <!-- Deposit / wallet as main payment hidden (align with pricing: no wallet UX)
          <button
            v-if="
              !isExpired() &&
              userStore.subscriptionType === 'CONSUMEASYOUPAY' &&
              userStore.subscriptionStartDate
            "
            @click="() => setDepositActivePayment(true)"
            class="bg-white text-xs md:text-base text-slate-900 font-semibold py-1 px-1 md:px-6 rounded-lg h-10 mt-4 border-slate-400 border-2 w-[70%] md:w-[80%] lg:w-[45%]"
          >
            {{ $t("set-as-main-payment-source") }}
          </button>
          -->
        </div>
        <div
          v-if="
            userStore.subscriptionType != 'FREE' &&
            userStore.subscriptionType != 'CONSUMEASYOUPAY'
          "
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 mt-3"
        >
          <p class="text-[#061B44] font-semibold text-base md:text-2xl">
            {{ $t("usage-statistics") }}
          </p>
          <div class="pt-6">
            <div class="flex flex-row justify-between">
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{ $t("audio-transcription-usage") }}
              </p>
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{
                  getCurrentUsage(
                    getVolumeLimits()["speechToTextVolume"],
                    userStore.speechToTextVolume
                  )
                }}
                /
                {{ getVolumeLimits()["speechToTextVolume"] }} {{ t("min") }}
                <span class="text-green-600">
                  {{
                    getExtraUsage(
                      getVolumeLimits()["speechToTextVolume"],
                      userStore.speechToTextVolume
                    ) > 0
                      ? `(+${getExtraUsage(
                          getVolumeLimits()["speechToTextVolume"],
                          userStore.speechToTextVolume
                        )} ${t("extra")})`
                      : ""
                  }}
                </span>
              </p>
            </div>
            <div class="bg-slate-200 rounded-full h-2.5 w-full mt-2">
              <div
                class="bg-slate-800 h-2.5 rounded-full"
                :style="{
                  width: `${computePercentage(
                    userStore.speechToTextVolume,
                    getVolumeLimits()['speechToTextVolume']
                  )}%`,
                }"
              ></div>
            </div>
          </div>
          <div class="pt-6">
            <div class="flex flex-row justify-between">
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{ $t("text-translation-usage") }}
              </p>
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{
                  getCurrentUsage(
                    getVolumeLimits()["textTranslationVolume"],
                    userStore.textTranslationVolume
                  )
                }}
                /
                {{ getVolumeLimits()["textTranslationVolume"] }}
                {{ t("chars") }}
                <span class="text-green-600">
                  {{
                    getExtraUsage(
                      getVolumeLimits()["textTranslationVolume"],
                      userStore.textTranslationVolume
                    ) > 0
                      ? `(+${getExtraUsage(
                          getVolumeLimits()["textTranslationVolume"],
                          userStore.textTranslationVolume
                        )} ${t("extra")})`
                      : ""
                  }}
                </span>
              </p>
            </div>
            <div class="bg-slate-200 rounded-full h-2.5 w-full mt-2">
              <div
                class="bg-slate-800 h-2.5 rounded-full"
                :style="{
                  width: `${computePercentage(
                    userStore.textTranslationVolume,
                    getVolumeLimits()['textTranslationVolume']
                  )}%`,
                }"
              ></div>
            </div>
          </div>
          <div class="pt-6">
            <div class="flex flex-row justify-between">
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{ $t("text-to-speech-usage") }}
              </p>
              <p class="text-xs md:text-sm text-slate-700 font-medium">
                {{
                  getCurrentUsage(
                    getVolumeLimits()["textToSpeechVolume"],
                    userStore.textToSpeechVolume
                  )
                }}
                /
                {{ getVolumeLimits()["textToSpeechVolume"] }} {{ t("chars") }}
                <span class="text-green-600">
                  {{
                    getExtraUsage(
                      getVolumeLimits()["textToSpeechVolume"],
                      userStore.textToSpeechVolume
                    ) > 0
                      ? `(+${getExtraUsage(
                          getVolumeLimits()["textToSpeechVolume"],
                          userStore.textToSpeechVolume
                        )} ${t("extra")})`
                      : ""
                  }}
                </span>
              </p>
            </div>
            <div class="bg-slate-200 rounded-full h-2.5 w-full mt-2">
              <div
                class="bg-slate-800 h-2.5 rounded-full"
                :style="{
                  width: `${computePercentage(
                    userStore.textToSpeechVolume,
                    getVolumeLimits()['textToSpeechVolume']
                  )}%`,
                }"
              ></div>
            </div>
          </div>
        </div>
        <!-- User deposit / wallet balance block hidden (align with pricing)
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 my-3"
        >
          <div class="flex flex-row space-x-2 items-center">
            <p class="text-[#061B44] font-semibold text-base md:text-2xl">
              {{ $t("user-deposit") }}
            </p>
            <div
              v-if="userStore.subscriptionType === 'CONSUMEASYOUPAY'"
              class="text-blue-700 flex flex-row font-medium items-center space-x-1 bg-blue-100 py-1 px-4 w-min text-nowrap text-xs h-min rounded-full"
            >
              <i
                class="pi pi-star-fill text-blue-500 text-xs cursor-pointer hover:text-slate-800"
              ></i>
              <p>{{ $t("main-source") }}</p>
            </div>
          </div>
          <div class="flex flex-row w-full justify-between mt-3">
            <div class="flex flex-col">
              <p class="text-slate-600 font-normal text-base">
                {{ $t("current-balance") }}
              </p>
              <p class="text-green-500 font-bold text-lg md:text-2xl">
                ${{ userStore.userDeposit }}
              </p>
            </div>
            <button
              @click="navigate('pricing')"
              class="bg-subDark text-white font-semibold py-1 px-2 md:px-6 rounded-lg h-10 mt-4 text-xs md:text-base"
            >
              {{ $t("add-credit-0") }}
            </button>
          </div>
          <button
            v-if="
              userStore.userDeposit > 0 &&
              userStore.subscriptionType != 'CONSUMEASYOUPAY'
            "
            @click="() => setDepositActivePayment(false)"
            class="bg-white text-xs md:text-base text-slate-900 font-semibold py-1 px-1 md:px-6 rounded-lg h-10 mt-4 border-slate-400 border-2 w-[70%] md:w-[80%] lg:w-[45%]"
          >
            {{ $t("set-as-main-payment-source") }}
          </button>
        </div>
        -->
        <!-- "Current main payment source" block hidden (wallet / deposit UX removed)
        <div
          v-if="
            userStore.subscriptionType != 'FREE' &&
            userStore.subscriptionType !== 'CONSUMEASYOUPAY'
          "
          class="flex flex-col container bg-blue-50 border-[1.5px] border-blue-300 rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 mt-3"
        >
          <div class="flex flex-row space-x-2 items-center mb-2">
            <i
              class="pi pi-star-fill text-blue-700 text-xs cursor-pointer hover:text-slate-800"
            ></i>
            <p class="text-blue-700 font-semibold text-base">
              {{ $t("current-main-payment-source") }}
            </p>
          </div>
          <p class="text-base text-blue-700">
            {{ t("your-package-subscription-will") }}
          </p>
        </div>
        -->
        <div
          v-if="userStore.subscriptionType != 'FREE'"
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[80%] 2xl:w-[50%] pt-2 mt-3 mb-5 md:mb-0"
        >
          <p class="text-[#061B44] font-semibold text-base md:text-2xl">
            {{ $t("Options") }}
          </p>
          <div class="flex flex-row justify-between text-xs md:text-base">
            <button
              @click="navigate('pricing')"
              class="bg-[#6193F6] text-white font-semibold py-1 px-1 md:px-6 rounded-lg w-[65%] md:w-[80%] lg:w-[45%] h-10 mt-4"
            >
              {{ $t("upgrade-subscription") }}
            </button>
            <button
              v-if="userStore.subscriptionId"
              @click="cancelSubscription"
              class="bg-white text-slate-900 font-semibold py-1 px-1 md:px-6 rounded-lg h-10 mt-4 border-slate-400 border-2 w-[65%] md:w-[80%] lg:w-[45%]"
            >
              {{ $t("cancel-subscription") }}
            </button>
          </div>
        </div>
      </div>
      <!-- contact tab -->
      <div
        v-if="showPasswordPopup"
        class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75"
      >
        <div
          class="container bg-white border border-black rounded-xl h-auto w-[90%] lg:w-[40%] flex flex-col items-center p-8"
        >
          <div class="flex flex-col w-[85%] lg:w-[90%]">
            <p class="text-lg text-slate-900 font-semibold pb-4">
              {{ $t("please-insert-your-password-to-continue") }}
            </p>
            <div class="relative h-10">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600 h-10"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="M6 6c0-3.311 2.689-6 6-6s6 2.688 6 6v4h3v14h-18v-14h3v-4zm14 5h-16v12h16v-12zm-13-5v4h10v-4c0-2.76-2.24-5-5-5s-5 2.24-5 5z"
                  />
                </svg>
              </span>
              <input
                v-model="currentPass"
                :type="viewPassword ? 'text' : 'password'"
                class="bg-gray-50 pl-10 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
                :placeholder="$t('Password')"
                required
              />
              <button
                type="button"
                :class="{ 'pr-4': locale === 'ar' }"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
                @click="togglePassword"
              >
                <svg
                  v-if="viewPassword"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                    fill-rule="nonzero"
                  />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                    fill-rule="nonzero"
                  />
                </svg>
              </button>
            </div>
            <div class="container flex flex-row space-x-4 pt-8">
              <button
                @click="updateEmail"
                type="button"
                class="bg-[#4867C8] text-white rounded-md py-1 w-[50%] lg:w-[30%] h-10"
              >
                <p class="font-semibold text-lg">{{ $t("save") }}</p>
              </button>
              <button
                @click="togglePasswordPopup"
                type="button"
                class="bg-white text-slate-900 border-slate-400 border-2 rounded-md py-1 w-[50%] lg:w-[30%] h-10"
              >
                <p class="font-semibold text-lg">{{ $t("cancel") }}</p>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="tabIndex == 4" class="w-[90%] space-y-4 pt-10 lg:pt-0">
        <p class="text-[#061B44] font-bold text-base md:text-2xl">{{ $t("Contact") }}</p>
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[85%] 2xl:w-[60%] pt-2 mt-8"
        >
          <div class="flex flex-col container w-full">
            <p class="text-base text-slate-500 font-medium pb-4">
              {{ $t("Email") }}
            </p>
            <input
              v-model="email"
              type="text"
              class="bg-gray-50 border w-full focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg p-4 h-10"
              :placeholder="$t('Email')"
              required="true"
            />
          </div>
          <p v-if="isContactError" class="font-semibold text-red-500 text-lg">
            {{ errorContactMessage }}
          </p>
          <div class="container flex flex-row justify-between space-x-4 pt-6">
            <button
              @click="requestEmailData"
              type="button"
              class="w-[45%] lg:w-[35%] text-white flex justify-center items-center space-x-1 lg:space-x-4 bg-[#6193F6] h-10 px-2 py-1 lg:px-3 rounded-lg"
            >
              <div class="flex flex-row items-center justify-center">
                <p
                  :class="{ 'px-2': locale === 'ar' }"
                  class="font-semibold text-base md:text-lg"
                >
                  {{ $t("save") }}
                </p>
              </div>
            </button>
            <div v-if="locale === 'ar'" class="w-2"></div>
            <button
              v-if="!isVerified"
              @click="requestNewVerificationEmail"
              type="button"
              class="w-[45%] lg:w-[35%] flex justify-center items-center space-x-1 lg:space-x-4 bg-white text-slate-900 border-slate-400 border-2 h-10 px-2 py-1 lg:px-3 rounded-lg"
            >
              <div class="flex flex-row items-center justify-center">
                <p
                  :class="{ 'px-2': locale === 'ar' }"
                  class="font-semibold text-base md:text-lg"
                >
                  {{ $t("verify") }}
                </p>
              </div>
            </button>
            <div v-if="locale === 'ar'" class="w-2"></div>
            <button
              @click="fetchProfile"
              type="button"
              :disabled="isLoading"
              class="w-[45%] lg:w-[35%] flex justify-center items-center space-x-1 lg:space-x-4 bg-white text-slate-900 border-slate-400 border-2 h-10 px-2 py-1 lg:px-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div class="flex flex-row items-center justify-center">
                <p
                  :class="{ 'px-2': locale === 'ar' }"
                  class="font-semibold text-base md:text-lg"
                >
                  {{ $t("cancel") }}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <!-- security tab -->
      <div v-if="tabIndex == 1" class="w-[90%] space-y-4 pt-10 lg:pt-0 pb-10">
        <p class="text-[#061B44] font-bold text-base md:text-2xl">
          {{ $t("security-settings") }}
        </p>
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full lg:w-[90%] pt-2 mt-8"
        >
          <p class="text-[#061B44] font-semibold text-base md:text-2xl">
            {{ $t("change-password") }}
          </p>
          <div class="container">
            <div class="flex flex-col border p-4 rounded-xl my-4">
              <p class="text-gray-500 mb-4 text-base font-normal">
                {{
                  $t(
                    "to-change-your-password-please-fill-in-the-fields-below-your-new-password-must-meet-the-following-criteria"
                  )
                }}
              </p>
              <ul class="list-disc pl-6">
                <li class="text-gray-500 mb-4 text-base font-normal">
                  {{ $t("it-must-be-at-least-8-characters-long") }}
                </li>
                <li class="text-gray-500 mb-4 text-base font-normal">
                  {{
                    $t(
                      "it-should-include-a-combination-of-uppercase-and-lowercase-letters-numbers-and-special-characters-for-increased-security"
                    )
                  }}
                </li>
                <li class="text-gray-500 mb-4 text-base font-normal">
                  {{
                    $t(
                      "avoid-using-easily-guessable-passwords-such-as-common-words-or-sequences-of-numbers"
                    )
                  }}
                </li>
                <li class="text-gray-500 mb-4 text-base font-normal">
                  {{
                    $t(
                      "remember-to-keep-your-password-confidential-and-do-not-share-it-with-anyone"
                    )
                  }}
                </li>
                <li class="text-gray-500 text-base font-normal">
                  {{
                    $t(
                      "your-password-is-fully-encrypted-on-our-servers-to-read-more-click"
                    )
                  }}
                  <a href="/privacy-policy" class="text-blue-500 font-semibold">
                    {{ $t("here") }}
                  </a>
                  {{ $t("to-see-our-privacy-policy-page") }}
                </li>
              </ul>
            </div>
            <p class="text-gray-500 mb-4 text-lg font-normal">
              {{
                $t(
                  "once-youve-entered-your-new-password-click-the-change-password-button-to-update-your-account-credentials-if-you-encounter-any-issues-or-have-questions-please-contact-our-support-team-for-assistance"
                )
              }}
            </p>
          </div>

          <div class="flex flex-col container w-[95%]">
            <p class="text-base text-slate-500 font-medium h-10">
              {{ $t("current-password") }}
            </p>
            <div class="relative">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600 h-10"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="M6 6c0-3.311 2.689-6 6-6s6 2.688 6 6v4h3v14h-18v-14h3v-4zm14 5h-16v12h16v-12zm-13-5v4h10v-4c0-2.76-2.24-5-5-5s-5 2.24-5 5z"
                  />
                </svg>
              </span>
              <input
                v-model="currentPass"
                :type="viewPassword ? 'text' : 'password'"
                :class="{ 'pr-10': locale === 'ar' }"
                class="bg-gray-50 pl-10 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
                :placeholder="$t('Password')"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
                @click="togglePassword"
              >
                <svg
                  v-if="viewPassword"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                    fill-rule="nonzero"
                  />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                    fill-rule="nonzero"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="flex flex-col container w-[95%] mt-4">
            <p class="text-base text-slate-500 font-medium pb-2">
              {{ $t("new-password") }}
            </p>
            <div class="relative">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600 h-10"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="M6 6c0-3.311 2.689-6 6-6s6 2.688 6 6v4h3v14h-18v-14h3v-4zm14 5h-16v12h16v-12zm-13-5v4h10v-4c0-2.76-2.24-5-5-5s-5 2.24-5 5z"
                  />
                </svg>
              </span>
              <input
                v-model="newPass"
                :type="viewPassword ? 'text' : 'password'"
                :class="{ 'pr-10': locale === 'ar' }"
                class="bg-gray-50 pl-10 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
                :placeholder="$t('new-password')"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
                @click="togglePassword"
              >
                <svg
                  v-if="viewPassword"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                    fill-rule="nonzero"
                  />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                    fill-rule="nonzero"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex flex-col container w-[95%] mt-4">
            <p class="text-base text-slate-500 font-medium pb-2">
              {{ $t("Confirm_password") }}
            </p>
            <div class="relative">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600 h-10"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="M6 6c0-3.311 2.689-6 6-6s6 2.688 6 6v4h3v14h-18v-14h3v-4zm14 5h-16v12h16v-12zm-13-5v4h10v-4c0-2.76-2.24-5-5-5s-5 2.24-5 5z"
                  />
                </svg>
              </span>
              <input
                v-model="confirmPass"
                :type="viewPassword ? 'text' : 'password'"
                :class="{ 'pr-10': locale === 'ar' }"
                class="bg-gray-50 pl-10 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
                :placeholder="$t('Confirm_password')"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
                @click="togglePassword"
              >
                <svg
                  v-if="viewPassword"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                    fill-rule="nonzero"
                  />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                    fill-rule="nonzero"
                  />
                </svg>
              </button>
            </div>
          </div>
          <!-- <p v-if="isSecurityError" class="font-semibold text-red-500 text-lg">
            {{ errorSecurityMessage }}
          </p> -->
          <div class="container flex flex-row space-x-4 pt-6 md:pb-10 lg:pb-0">
            <button
              @click="updatePassword"
              type="button"
              class="bg-[#4867C8] text-white rounded-md py-2 px-4 flex items-center justify-center w-[95%] h-10"
            >
              <div
                :class="{ 'flex-row-reverse': locale === 'ar' }"
                class="flex flex-row align-middle"
              >
                <p class="ml-2 font-semibold text-base md:text-lg">
                  {{ $t("change-password") }}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <!-- Payments tab -->
      <div
        v-if="tabIndex == 3"
        class="w-[90%] space-y-4 pt-10 lg:pt-0 pb-10 overflow-x-auto min-w-[400px]"
      >
        <div
          class="flex flex-col container bg-gray-50 border-[1.5px] rounded-xl p-4 w-full pt-2 mt-4"
        >
          <p class="text-[#061B44] font-bold text-base md:text-2xl">
            {{ $t("payments") }}
          </p>
          <!-- payments list -->
          <div class="flex flex-col h-min rounded-2xl my-4 w-full">
            <div class="overflow-x-scroll">
          <div class="flex flex-row border-b pb-2 text-nowrap w-full">
                <p class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[20%]">
                  {{ $t("invoice-id") }}
                </p>
                <p class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[17%]">
                  {{ $t("date") }}
                </p>
                <p class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[17%]">
                  {{ $t("amount") }}
                </p>

                <p class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[25%]">
                  {{ $t("package") }}
                </p>
                <p class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[12%]">
                  {{ $t("status") }}
                </p>
              </div>
              <div v-if="paymentsList.length != 0">
                <div v-for="payment in paymentsList" :key="payment.orderId">
                  <div class="flex flex-row items-end py-2 border-b pb-2">
                    <p
                      class="text-sm font-bold text-slate-900 min-w-[120px] lg:w-[20%] cursor-pointer"
                    >
                      {{ shortenedOrderId(payment.orderId) }}
                    </p>
                    <p class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[17%]">
                      {{ formatDate(payment.date) }}
                    </p>
                    <p class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[17%]">
                      {{ payment.amount }} {{ payment.currency }}
                    </p>
                    <p class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[25%]">
                      {{ t(payment.package.toLowerCase()).toUpperCase() }}
                    </p>
                    <div
                      class="w-[12%] flex flex-row items-center justify-start"
                    >
                      <p
                        :class="{
                          'bg-orange-300 text-orange-900':
                            payment.status == 'Refunded', // TODO update those
                          'bg-yellow-300 text-orange-900':
                            payment.status == 'pending',
                          'bg-green-300 text-green-900':
                            payment.status == 'succeeded',
                          'bg-red-300 text-red-900': payment.status == 'failed',
                        }"
                        class="text-sm font-semibold px-2 rounded-lg"
                      >
                        {{
                          payment.status === "Refunded"
                            ? t("Refunded")
                            : payment.status === "failed"
                            ? t("Failed")
                            : payment.status === "pending"
                            ? t("pending")
                            : t("Delivered")
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="paymentsList.length == 0 && !isLoading"
                class="flex items-center justify-center pt-80 h-full"
              >
                <p class="text-lg text-slate-800 font-semibold">
                  {{
                    $t(
                      "empty-payment-history-keep-exploring-or-reach-out-if-you-need-assistance"
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
          <!-- navigation card -->
          <div class="flex flex-row items-center justify-between w-full">
            <button
              @click="getPreviousPage"
              type="button"
              class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8"
            >
              <p class="font-normal text-base">{{ $t("previous") }}</p>
            </button>
            <p class="text-sm md:text-base text-black font-normal px-2">
              {{ $t("page") }} {{ currentPage }} {{ $t("of") }}
              {{ totalPages }}
            </p>
            <button
              @click="getNextPage"
              type="button"
              class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8"
            >
              <p class="font-normal text-base">{{ $t("next") }}</p>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="container flex items-center justify-center pt-40 lg:pt-0"
    >
      <img
        class="w-10 h-10 lg:w-20 lg:h-20 animate-spin fill-[#061B44]"
        src="../assets/icons/loading-loader.svg"
        alt="Loading icon"
      />
    </div>
    <div
      v-if="showImageUpdate"
      class="absolute flex w-full h-full z-10 items-center justify-center bg-black bg-opacity-50"
    >
      <div
        v-if="!isSavingImage"
        class="flex flex-col items-center justify-center w-[80%] md:w-[70%] lg:w-[50%] xl:w-[40%] 2xl:w-[30%] border-[1px] rounded-sm bg-white p-6"
      >
        <div class="flex flex-row justify-between items-center gap-2 w-full mb-4">
          <p class="text-lg font-semibold min-w-0 flex-1">
            {{ $t("update-profile-picture") }}
          </p>
          <div class="flex flex-row items-center gap-2 shrink-0">
            <button
              v-if="hasProfilePictureOnAccount && !selectedImage"
              type="button"
              :disabled="isRemovingProfilePicture"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :title="$t('remove-profile-picture')"
              :aria-label="$t('remove-profile-picture')"
              @click="requestRemoveProfilePicture"
            >
              <i class="pi pi-trash"></i>
            </button>
            <i
              class="pi pi-times text-slate-600 text-lg cursor-pointer hover:text-slate-800"
              role="button"
              tabindex="0"
              :aria-label="$t('cancel')"
              @click="showImageUpdate = false"
              @keydown.enter.prevent="showImageUpdate = false"
              @keydown.space.prevent="showImageUpdate = false"
            ></i>
          </div>
        </div>
        <div class="w-full mb-4">
          <div
            v-if="!selectedImage"
            id="upload-area"
            class="flex flex-col h-52 items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:bg-slate-50"
            @dragover.prevent
            @drop.prevent="handleDrop"
            onclick="document.getElementById('file-input').click()"
          >
            <i
              id="upload-icon"
              class="pi pi-cloud-upload text-slate-400 text-4xl mb-2"
            ></i>
            <p id="upload-text" class="text-sm text-slate-500 text-center">
              {{ t("drag-and-drop-or-click-to-upload") }}
            </p>
            <input
              id="file-input"
              type="file"
              class="hidden"
              accept="image/*"
              @change="handleFileSelect"
            />
          </div>
          <div
            v-else
            class="border-2 border-dashed border-slate-300 rounded-md h-52 flex items-center justify-center relative"
          >
            <img
              :src="selectedImage"
              alt="Preview"
              class="w-36 h-36 rounded-full overflow-hidden object-cover"
            />
            <i
              class="pi pi-times text-white bg-red-500 rounded-full p-[8px] text-base cursor-pointer absolute top-10 right-1/3"
              @click="selectedImage = null"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 pt-2">
          <button
            class="flex justify-center items-center space-x-1 lg:space-x-4 bg-white text-slate-900 border-slate-400 border-2 h-10 px-6 py-1 lg:px-10 rounded-lg"
            @click="cancelProfilePicture"
          >
            {{ t("cancel") }}
          </button>
          <button
            class="text-white flex justify-center items-center space-x-1 lg:space-x-4 bg-[#6193F6] h-10 px-6 py-1 lg:px-10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSavingImage"
            @click="saveProfilePicture"
          >
            {{ t("save") }}
          </button>
        </div>
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center w-[80%] md:w-[70%] lg:w-[50%] xl:w-[40%] 2xl:w-[30%] border-[1px] rounded-sm bg-white p-6 py-10"
      >
        <img
          class="w-10 h-10 lg:w-20 lg:h-20 animate-spin fill-[#061B44]"
          src="../assets/icons/loading-loader.svg"
          alt="Loading icon"
        />
        <p class="text-base font-medium text-[#061B44] mt-4">
          {{ t("please-wait") }}
        </p>
      </div>
    </div>
    <div
      v-if="showRemoveProfilePictureConfirm"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="cancelRemoveProfilePictureConfirm"
    >
      <div
        :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        class="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 flex flex-col gap-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-photo-confirm-title"
      >
        <p
          id="remove-photo-confirm-title"
          class="text-base text-slate-800 font-medium"
        >
          {{ $t("remove-profile-picture-confirm-message") }}
        </p>
        <div class="flex flex-row justify-end gap-2 flex-wrap">
          <button
            type="button"
            class="px-4 py-2 rounded-lg border-2 border-slate-400 text-slate-900 hover:bg-slate-50"
            @click="cancelRemoveProfilePictureConfirm"
          >
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            :disabled="isRemovingProfilePicture"
            class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="confirmRemoveProfilePicture"
          >
            {{ $t("remove-profile-picture") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "../store/auth";
import {
  API_BASE_URL,
  CANCELSUBSCRIPTION,
  LOGOUT,
  ORDERSHISTORY,
  PAYMENTSHISTORY,
  RESENDVERIFICATION,
  SETACTIVEPAYMENT,
  UPDATEEMAIL,
  UPDATEPASSWORD,
  USERPROFILE,
  USERPROFILEPICTURE,
} from "../utils/appConst";
import { PROFILE_NAME_MAX_LENGTH } from "../utils/profileNameLimits";
import { getVolumeLimitsForSubscription } from "../utils/subscriptionVolumeLimits";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useUserProfile } from "../store/userProfile";
import { PaymentModel } from "../utils/paymentModel";
import { OrdersModel } from "../utils/ordersModel";
import { useI18n } from "vue-i18n";
import Cookies from "js-cookie";
import { googleTokenLogin } from "vue3-google-login";
import { useUserActivities } from "../store/userActivities";
import { useUserPreference } from "../store/userPreferences";
import { useAudioStore } from "../store/audioStore";
import { useVideoStore } from "../store/videoStore";
import { useYoutubeStore } from "../store/youtubeStore";
import imageCompression from "browser-image-compression";
import profileImage from "../assets/images/settings/profile.jpg";
import { useHead } from "@vueuse/head";

const userStore = useUserProfile();
const authStore = useAuthStore();
const audioStore = useAudioStore();
const videoStore = useVideoStore();
const youtubeStore = useYoutubeStore();
const activitesStore = useUserActivities();
const prefStore = useUserPreference();
const router = useRouter();
const route = useRoute();
const fname = ref("");
const lname = ref("");
const fullName = ref("");
const email = ref("");
const userCurrentEmail = ref("");
const currentPass = ref("");
const newPass = ref("");
const confirmPass = ref("");
const errorProfileMessage = ref("");
const errorContactMessage = ref("");
const errorSecurityMessage = ref("");
const isLoading = ref(false);
const tabIndex = ref(0);
const isError = ref(false);
const isProfileError = ref(false);
const isContactError = ref(false);
const isSecurityError = ref(false);
const showPasswordPopup = ref(false);
const viewPassword = ref(false);
const isAuthedviaGoogle = ref(false);
const showImageUpdate = ref(false);
const selectedImage = ref<any>(null);
const userImage = ref<any>(null);
/** True when API reports a stored profile picture (even if preview data failed to load). */
const hasProfilePictureOnAccount = ref(false);
const isSavingImage = ref<boolean>(false);
const isRemovingProfilePicture = ref(false);
const showRemoveProfilePictureConfirm = ref(false);
const isVerified = ref<boolean>(false);
const isStudent = ref<boolean>(false);
const isResendEmail = ref<boolean>(false);
const userProfileUrl = `${API_BASE_URL}${USERPROFILE}`;
const paymentsHistoryUrl = `${API_BASE_URL}${PAYMENTSHISTORY}`;
const updatePasswordUrl = `${API_BASE_URL}${UPDATEPASSWORD}`;
const updateEmailUrl = `${API_BASE_URL}${UPDATEEMAIL}`;
const logoutUrl = `${API_BASE_URL}${LOGOUT}`;
const cancelSubsUrl = `${API_BASE_URL}${CANCELSUBSCRIPTION}`;
const updateProfilePictureUrl = `${API_BASE_URL}${USERPROFILEPICTURE}`;
const resendVerificationUrl = `${API_BASE_URL}${RESENDVERIFICATION}`;
const setActivePaymentUrl = `${API_BASE_URL}${SETACTIVEPAYMENT}`;
const paymentsList = ref<PaymentModel[]>([]);
const { t, locale } = useI18n();
let currentPage = 1;
let totalPages = 0;

function settingsLog(message: string, data?: unknown) {
  if (import.meta.env.PROD) return;
  if (data !== undefined) {
    console.log(`[settings] ${message}`, data);
  } else {
    console.log(`[settings] ${message}`);
  }
}

onBeforeMount(async () => {
  settingsLog("onBeforeMount: loading profile & payments");
  await fetchProfile();
  await fetchPaymentsHistory();
  await userStore.getUserSubscription(authStore.token!);
  isAuthedviaGoogle.value = Cookies.get("authUsingGoogle") === "true";
  /* if (route.hash === "#orders") {
    tabIndex.value = 2;
  } */
});

const toggleTab = (index: number) => {
  if (showPasswordPopup.value) {
    showPasswordPopup.value = false;
  }
  tabIndex.value = index;
};

const saveProfilePicture = async () => {
  try {
    settingsLog("saveProfilePicture: start");
    isSavingImage.value = true;
    // resize image to 512x512 and compress it
    const file = base64ToFile(selectedImage.value, "profile-picture.png");
    const compressedImage = await resizeAndCompressImage(file);
    // send image to server side
    await uploadImageToServer(compressedImage);
    // show toast and close modal
    isSavingImage.value = false;
    showImageUpdate.value = false;
    selectedImage.value = null;
    toast(t("profile-picture-updated-successfully"), {
      autoClose: 1500,
      type: "success",
    });
    settingsLog("saveProfilePicture: success, modal closed");
  } catch (error) {
    console.log("saveProfilePictureChanges -> error", error);
    settingsLog("saveProfilePicture: error", error);
    isSavingImage.value = false;
    selectedImage.value = null;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const base64ToFile = (base64: any, filename: string) => {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

const resizeAndCompressImage = async (file: any) => {
  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 250,
    useWebWorker: true,
  };
  return await imageCompression(file, options);
};

const handleDrop = (event: any) => {
  event.preventDefault();
  const file = event.dataTransfer.files[0];
  if (!file) {
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    toast(t("the-file-you-selected-is-too-large-please"), {
      autoClose: 1500,
      type: "warning",
    });
    return;
  }
  const supportedImageTypes = [
    "image/jpeg", // JPEG images
    "image/png", // PNG images
    "image/bmp", // BMP images
  ];
  if (file && supportedImageTypes.includes(file.type)) {
    const reader = new FileReader();
    reader.onload = function (e) {
      selectedImage.value = e!.target!.result;
    };
    reader.readAsDataURL(file);
  } else {
    toast(t("the-file-you-selected-is-not-a-supported-image-type"), {
      autoClose: 1500,
      type: "warning",
    });
  }
};

function handleFileSelect(event: any) {
  const file = event.target.files[0];
  if (!file) {
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    toast(t("the-file-you-selected-is-too-large-please"), {
      autoClose: 1500,
      type: "warning",
    });
    return;
  }
  const supportedImageTypes = [
    "image/jpeg", // JPEG images
    "image/png", // PNG images
    "image/bmp", // BMP images
  ];
  if (file && supportedImageTypes.includes(file.type)) {
    const reader = new FileReader();
    reader.onload = function (e) {
      selectedImage.value = e!.target!.result;
    };
    reader.readAsDataURL(file);
  } else {
    toast(t("the-file-you-selected-is-not-a-supported-image-type"), {
      autoClose: 1500,
      type: "warning",
    });
  }
}

const cancelProfilePicture = () => {
  settingsLog("cancelProfilePicture: closing modal, clearing selection");
  selectedImage.value = null;
  showImageUpdate.value = false;
};

const removeProfilePicture = async () => {
  if (!hasProfilePictureOnAccount.value || isRemovingProfilePicture.value) {
    settingsLog("removeProfilePicture: skipped", {
      hasProfilePictureOnAccount: hasProfilePictureOnAccount.value,
      isRemovingProfilePicture: isRemovingProfilePicture.value,
    });
    return;
  }
  try {
    settingsLog("removeProfilePicture: DELETE request", {
      url: updateProfilePictureUrl,
    });
    isRemovingProfilePicture.value = true;
    const response = await fetch(updateProfilePictureUrl, {
      method: "DELETE",
      headers: {
        "ngrok-skip-browser-warning": "69420",
        Authorization: `Bearer ${authStore.token}`,
      },
    });
    settingsLog("removeProfilePicture: response status", response.status);
    if (response.status === 200) {
      userImage.value = null;
      hasProfilePictureOnAccount.value = false;
      showImageUpdate.value = false;
      selectedImage.value = null;
      toast(t("profile-picture-removed-successfully"), {
        autoClose: 2000,
        type: "success",
      });
      settingsLog("removeProfilePicture: success, local state cleared");
    } else {
      throw new Error("Failed to remove profile picture");
    }
  } catch (error) {
    console.error("removeProfilePicture:", error);
    settingsLog("removeProfilePicture: error", error);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  } finally {
    isRemovingProfilePicture.value = false;
  }
};

const requestRemoveProfilePicture = () => {
  if (!hasProfilePictureOnAccount.value || isRemovingProfilePicture.value) {
    return;
  }
  showRemoveProfilePictureConfirm.value = true;
};

const cancelRemoveProfilePictureConfirm = () => {
  showRemoveProfilePictureConfirm.value = false;
};

const confirmRemoveProfilePicture = () => {
  showRemoveProfilePictureConfirm.value = false;
  void removeProfilePicture();
};

const uploadImageToServer = async (imageFile: any) => {
  try {
    settingsLog("uploadImageToServer: PUT", {
      url: updateProfilePictureUrl,
      fileSize: imageFile?.size,
      fileType: imageFile?.type,
    });
    const formData = new FormData();
    formData.append("imageFile", imageFile, "profile-picture.png");
    const response = await fetch(updateProfilePictureUrl, {
      method: "PUT",
      body: formData,
      headers: {
        "ngrok-skip-browser-warning": "69420",
        Authorization: `Bearer ${authStore.token}`,
      },
    });

    settingsLog("uploadImageToServer: response status", response.status);
    if (response.status === 200) {
      // the image stored in the server
      userImage.value = await fileToBase64(imageFile);
      hasProfilePictureOnAccount.value = true;
      settingsLog("uploadImageToServer: success, hasProfilePictureOnAccount=true");
    } else {
      throw new Error("Failed to upload image");
    }
  } catch (error) {
    console.error("Error uploading image:", error);
    settingsLog("uploadImageToServer: error", error);
    throw error;
  }
};

const fileToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const fetchProfile = async () => {
  try {
    settingsLog("fetchProfile: GET", { url: userProfileUrl });
    isProfileError.value = false;
    isLoading.value = true;
    const response = await fetch(userProfileUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    settingsLog("fetchProfile: HTTP status", response.status);
    const result = await response.json();
    if (result.error) {
      settingsLog("fetchProfile: API error", result.error);
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          break;
      }
      isProfileError.value = false;
      errorProfileMessage.value = result.error;
      return;
    }

    if (result.message == "Success" && result.data) {
      fname.value = result.data.userFirstName;
      lname.value = result.data.userLastName;
      email.value = result.data.userEmail;
      userCurrentEmail.value = result.data.userEmail;
      fullName.value = fname.value + " " + lname.value;
      userImage.value = result.data.profilePicture;
      hasProfilePictureOnAccount.value = Boolean(result.data.profilePictureID);
      isVerified.value = result.data.isVerified;
      isStudent.value = result.data.isStudent;
      settingsLog("fetchProfile: success", {
        profilePictureID: result.data.profilePictureID ?? null,
        hasProfilePictureOnAccount: hasProfilePictureOnAccount.value,
        hasProfilePictureDataUrl: Boolean(result.data.profilePicture),
      });
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    settingsLog("fetchProfile: exception", error);
    isLoading.value = false;
    isProfileError.value = true;
  }
};

const logout = async () => {
  try {
    if (activitesStore.settingDropDown) {
      activitesStore.settingDropDown = false;
    }
    const response = await fetch(logoutUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    await response.json();

    userStore.subscriptionType = "";
    userStore.subscriptionStartDate = "";
    userStore.subscriptionEndDate = "";
    authStore.unseenCount = 0;
    resetAudioService();
    resetVideoService();
    resetYoutubeService();
    authStore.logout();
    router.push("/");
  } catch (error) {
    console.error("Authentication failed:", error);
  }
};

const resetAudioService = () => {
  audioStore.receivedCaption = false;
  audioStore.processingStarted = false;
  audioStore.notReadyYet = false;
  audioStore.originalText = "";
  audioStore.translatedText = "";
  audioStore.selectedAudio.name = "";
  audioStore.userInputAudioLink = "";
  audioStore.defaultOriginalText = "";
  audioStore.defaultTranslatedText = "";
  audioStore.isFromNotification = false;
  audioStore.isTargetLanguageSelected = false;
};

const resetVideoService = () => {
  videoStore.receivedCaption = false;
  videoStore.processingStarted = false;
  videoStore.originalVideoSubtitle = "";
  videoStore.prebuildVideoSubtitle = "";
  videoStore.translatedVideoSubtitle = "";
  videoStore.originalText = "";
  videoStore.translatedText = "";
  videoStore.selectedVideo.name = "";
  videoStore.userInputVideoLink = "";
  videoStore.isTargetLanguageSelected = false;
  videoStore.isFromNotification = false;
  videoStore.notReadyYet = false;
  videoStore.defaultOriginalVideoSubtitle = "";
  videoStore.defaultTranslatedVideoSubtitle = "";
  videoStore.defaultOriginalText = "";
  videoStore.defaultTranslatedText = "";
};

const resetYoutubeService = () => {
  youtubeStore.hasVideo = false;
  youtubeStore.isLoading = false;
  youtubeStore.isNotValidLink = false;
  youtubeStore.isTargetLanguageSelected = false;
  youtubeStore.captions = [];
  youtubeStore.activeCaptionLang = "";
  youtubeStore.availableCaptions = [];
  youtubeStore.userInput = "";
  youtubeStore.hasCaption = false;
  youtubeStore.originalVideoSubtitle = "";
  youtubeStore.prebuildVideoSubtitle = "";
  youtubeStore.translatedVideoSubtitle = "";
  youtubeStore.originalText = "";
  youtubeStore.translatedText = "";
  youtubeStore.defaultOriginalVideoSubtitle = "";
  youtubeStore.defaultTranslatedVideoSubtitle = "";
  youtubeStore.defaultOriginalText = "";
  youtubeStore.defaultTranslatedText = "";
  try {
    youtubeStore.player.stopVideo();
  } catch (error) {}
  youtubeStore.isFromNotification = false;
};

const shortenedOrderId = (orderId: string) => {
  return `${orderId.slice(0, 5)}***${orderId.slice(-2)}`;
};

const copyToClipboard = (orderId: string) => {
  navigator.clipboard
    .writeText(orderId)
    .then(() => {
      toast(t("order-id-copied-to-clipboard"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    })
    .catch((err) => {
      console.error("Failed to copy order ID:", err);
    });
};

const fetchPaymentsHistory = async () => {
  try {
    isError.value = false;
    isLoading.value = true;
    const response = await fetch(paymentsHistoryUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ page: currentPage }),
    });
    const result = await response.json();
    if (result.error) {
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          break;
      }
      isError.value = false;
      return;
    }

    const { payments, totalpages } = result;

    if (totalpages) {
      totalPages = totalpages;
    }

    if (payments && Array.isArray(payments)) {
      paymentsList.value = payments.map((element: any) => ({
        orderId: element.orderId,
        amount: element.amount,
        date: element.paymentDate,
        status: element.paymentStatus,
        method: element.paymentMethod,
        package: element.package,
        currency: element.currency,
      }));
    } else {
      paymentsList.value = [];
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    isError.value = true;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const updateProfile = async () => {
  try {
    if (fname.value.trim().length == 0 || lname.value.trim().length == 0) {
      errorProfileMessage.value = t(
        "please-ensure-that-both-first-and-last-names-are-provided-these-fields-cannot-be-left-empty"
      );
      isProfileError.value = true;
      toast(
        t(
          "please-ensure-that-both-first-and-last-names-are-provided-these-fields-cannot-be-left-empty"
        ),
        {
          autoClose: 2500,
          type: "error",
          theme: "light",
        }
      );
      return;
    }
    if (
      fname.value.trim().length > PROFILE_NAME_MAX_LENGTH ||
      lname.value.trim().length > PROFILE_NAME_MAX_LENGTH
    ) {
      toast(
        t("profile-name-exceeds-max-length", {
          max: PROFILE_NAME_MAX_LENGTH,
        }),
        {
          autoClose: 3000,
          type: "error",
          theme: "light",
        }
      );
      return;
    }
    isProfileError.value = false;
    isLoading.value = true;
    const response = await fetch(userProfileUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        fname: fname.value.trim(),
        lname: lname.value.trim(),
      }),
    });
    const result = await response.json();
    if (result.error === "PROFILE_NAME_TOO_LONG") {
      toast(
        t("profile-name-exceeds-max-length", {
          max: result.maxLength ?? PROFILE_NAME_MAX_LENGTH,
        }),
        {
          autoClose: 3000,
          type: "error",
          theme: "light",
        }
      );
      isLoading.value = false;
      return;
    }
    if (result.error) {
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          break;
      }
      errorProfileMessage.value = result.error;
      isProfileError.value = true;
      return;
    }
    if (result.message == "Success") {
      fullName.value = fname.value + " " + lname.value;
      toast(
        t(
          "your-changes-have-been-successfully-saved-and-your-profile-has-been-updated"
        ),
        {
          autoClose: 2500,
          type: "success",
          theme: "light",
        }
      );
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    isProfileError.value = true;
    toast(t("oops-an-error-occurred-please-try-again-later"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const requestEmailData = async () => {
  if (email.value.trim().length == 0 || !isValidEmail(email.value)) {
    toast(t("please-ensure-that-the-email-address-provided-is-valid"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
    return;
  }
  if (email.value.trim() == userCurrentEmail.value.trim()) {
    toast(t("please-provide-a-new"), {
      autoClose: 2500,
      type: "info",
      theme: "light",
    });
    return;
  }
  togglePasswordPopup();
};

const updateEmail = async () => {
  try {
    if (Cookies.get("authUsingGoogle") == "true") {
      // google system
      googleTokenLogin()
        .then(async (response) => {
          if (response.access_token) {
            toast(t("logging-in"), {
              autoClose: 2500,
              type: "info",
              theme: "light",
            });
            const reqResponse = await fetch(updateEmailUrl, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "69420",
              },
              body: JSON.stringify({
                credential: response.access_token,
                email: email.value,
              }),
            });

            const result = await reqResponse.json();
            if (result.message == "Success") {
              fullName.value = fname.value + " " + lname.value;
              toast(t("your-email-has-been-updated-successfully"), {
                autoClose: 2500,
                type: "success",
                theme: "light",
              });
            }
          }
        })
        .catch((error) => {
          console.error("Google authentication failed:", error);
          toast(t("oops-an-error-occurred-please-try-again-later"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
        });
    } else {
      // password system
      togglePasswordPopup();
      isError.value = false;
      isLoading.value = true;
      const response = await fetch(updateEmailUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authStore.token}`,
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          email: email.value,
          password: currentPass.value,
        }),
      });
      const result = await response.json();
      currentPass.value = "";
      if (result.error) {
        switch (result.error) {
          case "Email and password are required in the request body.":
            toast(t("email-password-required"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            break;
          case "The email you are trying to use is already in use. Please choose a different one.":
            toast(t("email-already-in-use"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            break;
          case "User not found":
            toast(t("user-not-found"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            break;
          case "Invalid password":
            toast(t("invalid-password"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            break;
          case "Students cannot change their educational email address.":
            toast(t("students-cannot-change"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            break;
          case "Invalid token":
          case "Authorization header missing":
            toast(
              t("your-session-has-expired-please-log-in-again-to-continue"),
              {
                autoClose: 2500,
                type: "error",
                theme: "light",
              }
            );
            await new Promise((resolve) => setTimeout(resolve, 2500));
            authStore.logout();
            router.push("/signing");
            break;
          default:
            toast(t("something-went-wrong"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
        }
        isLoading.value = false;
      } else if (result.message == "Success") {
        fullName.value = fname.value + " " + lname.value;
        userCurrentEmail.value = email.value;
        toast(t("your-email-has-been-updated-successfully"), {
          autoClose: 2500,
          type: "success",
          theme: "light",
        });
      }
      isLoading.value = false;
    }
  } catch (error) {
    isLoading.value = false;
    isError.value = true;
    toast(t("oops-an-error-occurred-please-try-again-later"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const updatePassword = async () => {
  try {
    isSecurityError.value = false;
    if (newPass.value != confirmPass.value) {
      toast(
        t(
          "the-new-password-and-confirmation-password-do-not-match-please-try-again"
        ),
        {
          autoClose: 2500,
          type: "error",
          theme: "light",
        }
      );
      return;
    }

    if (currentPass.value.trim().length === 0) {
      toast(t("please-enter-your-current-password"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }

    if (newPass.value.length < 8) {
      toast(
        t(
          "the-new-password-is-too-short-please-choose-a-password-that-is-at-least-8-characters-long"
        ),
        {
          autoClose: 2500,
          type: "error",
          theme: "light",
        }
      );
      return;
    }

    isLoading.value = true;
    const response = await fetch(updatePasswordUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        oldPass: currentPass.value,
        newPass: newPass.value,
      }),
    });
    const result = await response.json();

    if (result.error) {
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        case "Password must be at least 8 characters long.":
          toast(t("please-ensure-your-password-issecurity"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          break;
        case "Wrong password":
          toast(
            t(
              "the-current-password-you-entered-is-incorrect-please-verify-and-try-again"
            ),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
          break;
        default:
          break;
      }
      isLoading.value = false;
    }
    if (result.message == "Success") {
      toast(t("the-password-has-been-updated-successfully"), {
        autoClose: 2500,
        type: "success",
        theme: "light",
      });
    }
    currentPass.value = "";
    newPass.value = "";
    confirmPass.value = "";
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    isSecurityError.value = true;
    toast(t("oops-an-error-occurred-please-try-again-later"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const togglePassword = () => {
  viewPassword.value = !viewPassword.value;
};

const togglePasswordPopup = () => {
  showPasswordPopup.value = !showPasswordPopup.value;
};

function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

const navToCheckout = () => {
  activitesStore.setComingFrom(null);
  router.push("/checkout");
};

function getCurrentUsage(usage: number, userUsage: number): number {
  let result = usage - userUsage;
  if (usage - userUsage < 0) {
    result = 0;
  }
  return result;
}

function getExtraUsage(usage: number, userUsage: number): number {
  let result = usage - userUsage;
  if (usage - userUsage < 0) {
    return Math.abs(result);
  }
  return 0;
}

const formatDate = (dateString: string) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
};

const getNextPage = async () => {
  try {
    if (currentPage == totalPages) {
      return;
    }
    currentPage++;
    await fetchPaymentsHistory();
  } catch (error) {
    console.log("getNextPage: ", error);
  }
};
/* todo */
const getPreviousPage = async () => {
  try {
    if (currentPage == 1) {
      return;
    }
    currentPage--;
    await fetchPaymentsHistory();
  } catch (error) {
    console.log("getPreviousPage: ", error);
  }
};

const capitalizeFirstLetter = (text: string): string => {
  if (prefStore.local === "AR" || prefStore.local === "CH") {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

function getLastPackage(pack: string): string {
  if (pack === "consumeasyoupay") {
    if (!userStore.lastPackage) {
      return capitalizeFirstLetter("FREE");
    }
    return capitalizeFirstLetter(userStore.lastPackage);
  }
  return capitalizeFirstLetter(userStore.subscriptionType);
}

function computePercentage(usage: number, offermount: number): number {
  if (offermount === 0) {
    return 0;
  }
  const currentUage = getCurrentUsage(offermount, usage);
  return (100 * currentUage) / offermount;
}

function getVolumeLimits() {
  return getVolumeLimitsForSubscription(userStore.subscriptionType);
}

const navigate = (path: string) => {
  router.push(`/${path}`);
};

const isExpired = (): boolean => {
  if (!userStore.subscriptionEndDate) {
    return false;
  }
  const currentTimestamp = new Date(new Date().toISOString());
  const subscriptionEndDate = new Date(userStore.subscriptionEndDate);
  return currentTimestamp > subscriptionEndDate;
};

const setDepositActivePayment = async (isPackage: boolean) => {
  try {
    if (!isPackage && userStore.userDeposit === 0) {
      return;
    }
    if (!isPackage && userStore.subscriptionType === "CONSUMEASYOUPAY") {
      return;
    }
    if (isPackage && userStore.subscriptionType != "CONSUMEASYOUPAY") {
      return;
    }
    if (
      isPackage &&
      userStore.subscriptionType != "CONSUMEASYOUPAY" &&
      isExpired()
    ) {
      return;
    }
    const response = await fetch(setActivePaymentUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ isPackage }),
    });

    switch (response.status) {
      case 200:
        toast(t("your-primary-payment-method"), {
          autoClose: 1500,
          type: "success",
          theme: "light",
        });
        await userStore.getUserSubscription(authStore.token!);
        break;

      case 409:
        toast(t("subscription-already-active"), {
          autoClose: 1500,
          type: "success",
          theme: "light",
        });
        break;

      default:
        toast(t("oops-an-error-occurred-please-try-again-later"), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
    }
  } catch (error) {
    console.error("cancelSubscription failed:", error);
    toast(t("oops-an-error-occurred-please-try-again-later"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const cancelSubscription = async () => {
  try {
    if (activitesStore.settingDropDown) {
      activitesStore.settingDropDown = false;
    }
    if (!userStore.subscriptionId) return;
    const response = await fetch(cancelSubsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    if (response.status === 200) {
      toast(t("your-subscription-has-been-cancele"), {
        autoClose: 3500,
        type: "success",
        theme: "light",
      });
      userStore.getUserSubscription(authStore.token!);
    } else if (response.status === 400) {
      toast(t("you-dont-have-an-active-subscription-che"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    }
  } catch (error) {
    console.error("cancelSubscription failed:", error);
  }
};

const requestNewVerificationEmail = async () => {
  try {
    if (isResendEmail.value || isVerified.value) return;
    isResendEmail.value = true;
    const response = await fetch(resendVerificationUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
      },
    });
    const data = await response.json();
    if (response.status === 200) {
      toast(t("a-new-verification-email-has-been-sent-to-your-email-address"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    } else {
      switch (data.error || data.message) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        case "Too many requests. Please wait 1 minute before trying again.":
          toast(t("too-many-requests-please-wait"), {
            autoClose: 3000,
            type: "error",
            theme: "light",
          });
          break;
        case "Internal Server Error":
          toast(t('were-fixing-a-technical-issue-so-please-try'), {
            autoClose: 1500,
            type: "error",
            theme: "light",
          });
          break;
        default:
          toast(
            t("failed-to-send-a-new-verification-email-please-try-again-later"),
            {
              autoClose: 1500,
              type: "error",
              theme: "light",
            }
          );
          break;
      }
    }
    isResendEmail.value = false;
  } catch (error) {
    console.log("requestNewVerificationEmail:", error);
    isResendEmail.value = false;
  }
};

useHead({
  title:
    "Settings - Subtitles Tech"
});
</script>
